import { SearchFilters } from '@/utils/search-filters';
import { SetStateAction, useMemo } from 'react';
import { useFilteredCategoryCatalog } from './use-filtered-category-catalog';
import { useFilteredSearchCatalog } from './use-filtered-search-catalog';
import { VariantPackInfoContext_GroupBadgeFieldsFragment } from '@/types';

interface Props {
  badges: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  catalogCategoryListsEnabled: boolean;
  categoryCatalog: ReturnType<typeof useFilteredCategoryCatalog>;
  searchCatalog: ReturnType<typeof useFilteredSearchCatalog>;
  showSearchView: boolean;
}

/**
 * Consolidates filter state from search and category catalogs into a single SearchFilters object.
 * Selects the active catalog's filter values based on showSearchView,
 * while keeping both catalogs' filter state in sync via dual setters.
 */
export const useConsolidatedCatalogFilters = ({
  badges,
  categoryCatalog,
  searchCatalog,
  showSearchView,
  catalogCategoryListsEnabled,
}: Props): SearchFilters => {
  return useMemo(() => {
    const useSearchFilters = showSearchView || catalogCategoryListsEnabled;
    const selectedCatalog = useSearchFilters ? searchCatalog : categoryCatalog;

    return {
      badges,
      badgesFilter: useSearchFilters
        ? searchCatalog.badgesFilter
        : categoryCatalog.badgesFilter,
      gtinFilters: selectedCatalog.gtinFilters,
      searchFilters: searchCatalog.searchFilters,
      setBadgesFilter: (filters: SetStateAction<Set<string>>) => {
        categoryCatalog.setBadgesFilter(filters);
        searchCatalog.setBadgesFilter(filters);
      },
      // Update the subcategoriesFilter in both catalogs at the same time so they remain consistent
      setSubcategoriesFilter: (filters: SetStateAction<Set<string>>) => {
        categoryCatalog.setSubcategoriesFilter(filters);
        searchCatalog.setSubcategoriesFilter(filters);
      },
      subcategories: selectedCatalog.subcategories,
      subcategoriesFilter: selectedCatalog.subcategoriesFilter,
    };
  }, [
    showSearchView,
    catalogCategoryListsEnabled,
    searchCatalog,
    categoryCatalog,
    badges,
  ]);
};
